import { createContext, useContext } from 'react';
import { useParams } from 'react-router';
import { FunctionPermissions } from './models/FunctionPermissions';
import { PropertyAssociationArea } from './models/PropertyAssociationArea';
import { PropertyAssociationInfo } from './models/PropertyAssociationInfo';
import { PropertyOwner } from './models/PropertyOwner';

export type PropertyAssociationContextValue = {
    allPropertyAssociations: PropertyAssociationInfo[];
    allAreas: PropertyAssociationArea[];
    currentArea: PropertyAssociationArea | undefined;
    currentFunctionPermissions: FunctionPermissions | undefined;
    currentPropertyAssociation: PropertyAssociationInfo | undefined;
    currentPropertyAssociationId: string;
    currentPropertyOwner: PropertyOwner | undefined;
    isAdmin: boolean;
    isGuest: boolean;
    isRBHyr: boolean;
};

const PropertyAssociationContext = createContext<PropertyAssociationContextValue | undefined>(undefined);

export const PropertyAssociationProvider: React.FC<{
    children: React.ReactNode;
    value: PropertyAssociationContextValue;
}> = ({ children, value }) => {
    return <PropertyAssociationContext.Provider value={value}>{children}</PropertyAssociationContext.Provider>;
};

export const usePropertyAssociationContext = (): PropertyAssociationContextValue => {
    const context = useContext(PropertyAssociationContext);

    if (!context) {
        throw new Error('usePropertyAssociation must be used within PropertyAssociationProvider');
    }

    return context;
};

/**
 * When using the context outside of the PropertyAssociationLayout, we don't have the propertyAssociationId in the context yet. This hook allows us to get the current property association based on the URL param.
 * @returns The current property association or undefined if not found or if propertyAssociationId is not in the URL params.
 */
export const useCurrentPropertyAssociationFromRoute = (): PropertyAssociationInfo | undefined => {
    const { allPropertyAssociations } = usePropertyAssociationContext();
    const { propertyAssociationId } = useParams();

    if (!propertyAssociationId) {
        return undefined;
    }

    return allPropertyAssociations.find((pa) => pa.propertyAssociationId === propertyAssociationId);
};
